import type { Theme } from '@mui/material/styles';
import { ThemeType } from 'src/components/utils/theme/ThemeType';

const SCALE_FACTOR: Record<'s' | 'm' | 'l', number> = { s: 1, m: 1.25, l: 1.75 };

// MUI's defaults for a medium contained button when no tenant customization is set.
const MUI_DEFAULT_PADDING_X = 16;
const MUI_DEFAULT_PADDING_Y = 6;
const MUI_DEFAULT_BORDER_WIDTH = 1;

type BasePadding = { x?: number; y?: number };

// NOTE: values are emitted at every factor (including 1). At factor=1 the values
// compose with existing customizations / MUI defaults to a no-op visually, but
// they are still emitted so that an xs-size block (factor=1) can reset an outer
// scaled block (factor>1) — without explicit values, the cascade would leave the
// outer scale "stuck" at mobile widths.
function scaleStyle(
    factor: number,
    theme: Theme,
    customizations: ThemeType,
    basePadding: BasePadding | undefined,
) {
    const isOutlined = customizations.primaryButtonStyle === 'outlined';

    const baseFontSize = customizations.buttonFontSize ?? customizations.fontSize;
    const basePaddingX =
        basePadding?.x ??
        (isOutlined
            ? customizations.outlinedButtonLeftRightPadding
            : customizations.containedButtonLeftRightPadding) ??
        MUI_DEFAULT_PADDING_X;
    const basePaddingY = basePadding?.y ?? MUI_DEFAULT_PADDING_Y;
    const baseBorderWidth = customizations.outlinedButtonBorderWidth ?? MUI_DEFAULT_BORDER_WIDTH;

    return {
        fontSize: `calc(${baseFontSize}px * ${factor})`,
        // !important is required to beat MuiButton.contained/outlined theme overrides
        // in Theme.ts that set paddingLeft/Right (and outlined borderWidth) with !important.
        paddingLeft: `calc(${basePaddingX}px * ${factor}) !important`,
        paddingRight: `calc(${basePaddingX}px * ${factor}) !important`,
        paddingTop: `calc(${basePaddingY}px * ${factor})`,
        paddingBottom: `calc(${basePaddingY}px * ${factor})`,
        // Scale corners so larger buttons keep the same visual roundness ratio as today's
        // transform: scale(...), which uniformly scaled radii.
        borderRadius: `${customizations.borderRadius * factor}px`,
        // Outlined variant only: scale the border so today's transform-scaled border
        // thickness is preserved (a 1px border at scale(1.75) painted as ~1.75px).
        ...(isOutlined && {
            borderWidth: `calc(${baseBorderWidth}px * ${factor}) !important`,
        }),
    };
}

export function getBookingButtonSizeStyle(
    size: 's' | 'm' | 'l',
    xsSize: 's' | 'm' | 'l',
    theme: Theme,
    customizations: ThemeType,
    basePadding?: BasePadding,
) {
    return {
        ...scaleStyle(SCALE_FACTOR[size], theme, customizations, basePadding),
        [theme.breakpoints.down('sm')]: scaleStyle(
            SCALE_FACTOR[xsSize],
            theme,
            customizations,
            basePadding,
        ),
    };
}
